/**
 * One link, said as one sentence (step 1 of docs/plans/links-rca.md, L7 of
 * docs/plans/links-fixes.md):
 *
 *   When a Milk Sales entry is saved with Payment Status Paid and Payment
 *   Mode Cash, adds its Total Amount to Cash Register.
 *
 * Every condition is spelled and nothing is cut. A link is held in three
 * shapes (a stored link with its schemas, a studio draft by keys, a composed
 * setup by keys), and each has a thin adapter that turns its own shape into the
 * neutral `LinkSentenceInput` below, already in words. This file is the only
 * place that decides how those words are put together, so the Links page, the
 * map, the studio canvas, the phone and OS cards say a rule the same way.
 *
 * The direction word comes from what the row does in the target
 * (`linkRowSign`): a target that reads its direction off a field (a cash register's
 * Type) is moved by the choice the link fills that field with, and the link's
 * own sign is not read there (`applySign` in apps/server/src/links.ts).
 *
 * Pure: words come through `t`, figures through the shared formatters.
 */
import { formatMoney, formatNumber } from './format';
import { formulaOf, isComputedType, type FieldSpec } from './formula';
import { t } from './i18n/t';
import type { LinkConditionOp } from './links';

/** One condition, in words: the field asked about, the check, and each value already said. */
export type LinkSentenceCondition = {
  /** "Payment Mode", or for a hop "the customer’s Credit allowed". */
  readonly field: string;
  readonly op: LinkConditionOp;
  /** Each value in words: an option's label, a figure drawn, a name. */
  readonly values: readonly string[];
};

/**
 * Which way the row moves the target's figure: 1 adds, -1 takes, null when it
 * depends on each entry (the target's direction field is copied from the
 * source, or left for each entry to set, so one entry may add and the next
 * take).
 */
export type LinkRowDirection = 1 | -1 | null;

export type LinkSentenceInput = {
  /** The record that fires it. */
  readonly source: string;
  /** Every group must hold; inside a group any one is enough. */
  readonly groups: readonly (readonly LinkSentenceCondition[])[];
  readonly direction: LinkRowDirection;
  /** What it carries into the target's measure, in words; null when it carries no figure. */
  readonly measure: string | null;
  /**
   * True when the target keeps a total the row moves. Read only when `measure`
   * is null: a row that takes from a total whose figure the sentence cannot
   * name (a worked-out total with an input left unfilled) still says it takes,
   * rather than the bare "adds an entry" a note says. Absent or false keeps
   * those bare words, so a target with no total never reads as taking.
   */
  readonly totals?: boolean;
  /** The record it posts into. */
  readonly target: string;
};

/**
 * The sentence in parts, for a surface that draws some of them as controls
 * (the studio's record and condition tokens). Joining every part's `words`
 * gives the sentence `linkSentenceText` returns.
 */
export type LinkSentencePart =
  | { readonly kind: 'text'; readonly words: string }
  | { readonly kind: 'source'; readonly words: string }
  | { readonly kind: 'target'; readonly words: string }
  | { readonly kind: 'measure'; readonly words: string }
  | { readonly kind: 'cond'; readonly group: number; readonly index: number; readonly words: string }
  | { readonly kind: 'conj'; readonly conj: 'and' | 'or' | 'either'; readonly words: string };

/* ------------------------------------------------------------ the direction */

/**
 * The sign one row has in its target.
 *
 * `signsByField` is true when the target reads its direction off a choice
 * field (its `sign` binding names a field). Then `filledSign` is what the link
 * fills that field with: the sign of the fixed choice it writes (1 or -1), null
 * when it writes a choice that carries no sign or copies the field from the
 * entry, and undefined when it does not fill it. The server never applies the
 * link's own sign to such a target (`applySign` in apps/server/src/links.ts),
 * so a link that leaves the field unfilled has no direction of its own: each
 * entry's field sets it, and the row's direction is null, as for a copied one.
 * Otherwise the link's own sign moves the figure.
 */
export const linkRowSign = (sign: 1 | -1, signsByField: boolean, filledSign?: 1 | -1 | null): LinkRowDirection => {
  if (!signsByField) return sign;
  return filledSign ?? null;
};

/* ---------------------------------------------------------------- the words */

/** A run of values: "Cash", "Cash or UPI", "Cash, UPI or Card". */
export const linkValuesWords = (values: readonly string[]): string => {
  if (values.length <= 1) return values[0] ?? '';
  return `${values.slice(0, -1).join(', ')} ${t('link.or')} ${values.at(-1) ?? ''}`;
};

/** One condition as a clause: "Payment Mode Cash", "Amount at least ₹500", "Notes empty". */
export const linkConditionWords = (condition: LinkSentenceCondition): string =>
  t(`link.clause.${condition.op}`, { field: condition.field, value: condition.values.length === 0 ? '…' : linkValuesWords(condition.values) });

/**
 * A first letter that takes "an" in English. A translation that has one article
 * gives both keys the same words, so nothing here is English-only in effect.
 */
const takesAn = (name: string): boolean => /^[aeiou]/i.test(name.trim());

// Stands in for a part inside a translated sentence, so a translator may put
// the parts in any order and the parts still come back as parts.
const MARK = '\u0000';
const mark = (name: string): string => `${MARK}${name}${MARK}`;

const splitMarked = (said: string, fill: (name: string) => LinkSentencePart[]): LinkSentencePart[] =>
  said
    .split(MARK)
    .flatMap((piece, at): LinkSentencePart[] => (at % 2 === 1 ? fill(piece) : piece === '' ? [] : [{ kind: 'text', words: piece }]));

/** The conditions as parts: groups joined by "and", a group's alternatives by "either … or". */
export const linkConditionParts = (groups: LinkSentenceInput['groups']): LinkSentencePart[] => {
  const parts: LinkSentencePart[] = [];
  const kept = groups.map((group, at) => ({ group, at })).filter((one) => one.group.length > 0);
  kept.forEach(({ group, at }, place) => {
    if (place > 0) parts.push({ kind: 'text', words: ' ' }, { kind: 'conj', conj: 'and', words: t('link.and') }, { kind: 'text', words: ' ' });
    // "either" only where an "and" sits beside it: alone, "A or B" cannot be misread.
    if (group.length > 1 && kept.length > 1) parts.push({ kind: 'conj', conj: 'either', words: t('link.or_either') }, { kind: 'text', words: ' ' });
    group.forEach((condition, index) => {
      if (index > 0) parts.push({ kind: 'text', words: ' ' }, { kind: 'conj', conj: 'or', words: t('link.or') }, { kind: 'text', words: ' ' });
      parts.push({ kind: 'cond', group: at, index, words: linkConditionWords(condition) });
    });
  });
  return parts;
};

/** The conditions alone, in words; '' when the link fires on every entry. */
export const linkConditionsText = (groups: LinkSentenceInput['groups']): string =>
  linkConditionParts(groups)
    .map((part) => part.words)
    .join('');

/** The whole rule, in parts. */
export const linkSentenceParts = (input: LinkSentenceInput): LinkSentencePart[] => {
  const conditions = linkConditionParts(input.groups);
  const an = takesAn(input.source);
  const when =
    conditions.length === 0
      ? t(an ? 'link.sentence.when_an' : 'link.sentence.when_a', { record: mark('source') })
      : t(an ? 'link.sentence.when_an_with' : 'link.sentence.when_a_with', { record: mark('source'), conditions: mark('conditions') });
  const outcome =
    input.measure === null
      ? t(input.totals === true && input.direction === -1 ? 'link.sentence.writes_takes' : 'link.sentence.writes', {
          when: mark('when'),
          record: mark('target'),
        })
      : t(input.direction === 1 ? 'link.sentence.adds' : input.direction === -1 ? 'link.sentence.takes' : 'link.sentence.posts', {
          when: mark('when'),
          measure: mark('measure'),
          record: mark('target'),
        });
  const fill = (name: string): LinkSentencePart[] => {
    switch (name) {
      case 'source':
        return [{ kind: 'source', words: input.source }];
      case 'target':
        return [{ kind: 'target', words: input.target }];
      case 'measure':
        return input.measure === null ? [] : [{ kind: 'measure', words: input.measure }];
      case 'conditions':
        return conditions;
      case 'when':
        return splitMarked(when, fill);
      default:
        return [];
    }
  };
  return splitMarked(outcome, fill);
};

/** The whole rule, in one sentence. */
export const linkSentenceText = (input: LinkSentenceInput): string =>
  linkSentenceParts(input)
    .map((part) => part.words)
    .join('');

/* ---------------------------------------------------------------- figures */

/** What a figure needs of its field: its type, and a worked-out field's recipe. */
export type FigureField = { readonly type: string; readonly spec?: FieldSpec | null };

/** Whether a field holds money or a plain number, a worked-out field by what it works out to; null for anything else. */
const figureOf = (field: FigureField): 'money' | 'number' | null => {
  const type = isComputedType(field.type) ? formulaOf(field.spec)?.result : field.type;
  if (type === 'money') return 'money';
  return type === 'number' || type === 'quantity' ? 'number' : null;
};

/**
 * One value on a money or number field, drawn by the shared formatters whether
 * the rule stored it as a number or as its digits: "₹1", "1,50,000", never the
 * bare digits. Null for a field that holds no figure, or a value that is not one.
 */
export const figureWords = (field: FigureField, value: string | number, currency: string | null): string | null => {
  const figure = typeof value === 'number' ? value : value.trim() === '' ? Number.NaN : Number(value);
  if (!Number.isFinite(figure)) return null;
  const kind = figureOf(field);
  if (kind === 'money') return formatMoney(figure, currency);
  return kind === 'number' ? formatNumber(figure) : null;
};
